import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { EventId, RegionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { EventListEntryDto } from "components/calendar/calendar-types";
import { inviteeSummaryText } from "components/calendar/invitee-copy";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import { Badge } from "components/ui/badge";
import { BlankSlate } from "components/ui/blank-slate";
import { defaultCardStyle } from "components/ui/card";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";
import { eventStatusBadge, formatEventDay, formatEventTime } from "./event-format";
import { NewEventPanel } from "./new-event-panel";

export const EventsTab: React.FC = () => {
    const regionId = useRegionId();
    const history = useHistory();
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.event.list.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: !!regionId,
    });
    const options = useActivityTypeOptions();
    const typeNames = useMemo(
        () => new Map(options.map(option => [option.id as string, option.name])),
        [options],
    );
    const [now] = useState(() => Date.now());
    const [creating, setCreating] = useState(false);
    const handleNewEvent = useCallback(() => setCreating(true), []);
    const handleEditorClose = useCallback(() => setCreating(false), []);
    const handleCreated = useCallback(
        (eventId: EventId) => history.push(`/events/${eventId}`),
        [history],
    );
    const { upcoming, past } = useMemo(() => {
        const events = query.data ?? [];
        return {
            upcoming: events.filter(event => new Date(event.startsAt).getTime() >= now),
            past: events
                .filter(event => new Date(event.startsAt).getTime() < now)
                .reverse(),
        };
    }, [query.data, now]);

    return (
        <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                    Marketing events with guest lists and RSVP tracking.
                </p>
                <PrimaryButton onClick={handleNewEvent}>New event</PrimaryButton>
            </div>
            {query.isPending ? <Spinner /> : null}
            {query.isError ? (
                <p className="text-sm text-red-600">
                    Could not load events. Please try again.
                </p>
            ) : null}
            {query.data && query.data.length === 0 ? (
                <BlankSlate>
                    No events yet — create one to invite your referral partners.
                </BlankSlate>
            ) : null}
            {upcoming.length > 0 ? (
                <EventListSection
                    title="Upcoming"
                    events={upcoming}
                    typeNames={typeNames}
                />
            ) : null}
            {past.length > 0 ? (
                <EventListSection title="Past" events={past} typeNames={typeNames} />
            ) : null}
            {creating ? (
                <NewEventPanel onCreated={handleCreated} onClose={handleEditorClose} />
            ) : null}
        </div>
    );
};

interface EventListSectionProps {
    title: string;
    events: EventListEntryDto[];
    typeNames: Map<string, string>;
}

const EventListSection: React.FC<EventListSectionProps> = props => {
    const { title, events, typeNames } = props;
    return (
        <div className={`overflow-hidden ${defaultCardStyle}`}>
            <div className="border-b border-gray-200 px-4 py-2 text-base font-medium text-gray-900">
                {title}
            </div>
            {events.map(event => (
                <EventListRow key={event.id} event={event} typeNames={typeNames} />
            ))}
        </div>
    );
};

interface EventListRowProps {
    event: EventListEntryDto;
    typeNames: Map<string, string>;
}

const EventListRow: React.FC<EventListRowProps> = props => {
    const { event, typeNames } = props;
    const day = formatEventDay(event);
    const badge = eventStatusBadge(event.status);
    const typeName = event.activityTypeId
        ? (typeNames.get(event.activityTypeId) ?? null)
        : null;
    const meta = [
        typeName,
        formatEventTime(event),
        inviteeSummaryText(event.inviteeStats),
    ]
        .filter(part => part !== null && part !== "")
        .join(" · ");
    return (
        <Link
            to={`/events/${event.id}`}
            className="twoverride flex w-full items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-left last:border-0 hover:bg-gray-50">
            <div className="w-10 shrink-0 rounded-md border border-gray-200 bg-gray-50 py-1 text-center">
                <div className="text-xs uppercase text-gray-500">{day.month}</div>
                <div className="text-sm font-medium text-gray-900">{day.day}</div>
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900">
                        {event.title}
                    </span>
                    {badge && badge.label !== "Scheduled" ? (
                        <Badge type={badge.type} value={badge.label} />
                    ) : null}
                </div>
                <div className="truncate text-xs text-gray-500">{meta}</div>
            </div>
            <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
        </Link>
    );
};
